/**
 * BootstrapPreloader.jsx
 * 
 * Este componente garante que o Bootstrap seja corretamente carregado e inicializado
 * antes que os componentes que dependem dele sejam renderizados.
 * 
 * Versão otimizada para garantir que os menus dropdown do Bootstrap funcionem corretamente.
 */
import { useEffect } from 'react';

/**
 * Componente que pré-carrega e inicializa o Bootstrap para evitar problemas
 * com modais, tooltips, dropdowns e outros componentes interativos.
 */
const BootstrapPreloader = () => {
  // Função para inicializar os dropdowns após o carregamento do Bootstrap
  const initializeDropdowns = () => {
    if (typeof window !== 'undefined' && window.bootstrap && window.bootstrap.Dropdown) {
      // Encontra todos os elementos de dropdown
      const dropdownElements = document.querySelectorAll('[data-bs-toggle="dropdown"]');
      
      if (dropdownElements.length > 0) {
        dropdownElements.forEach(element => {
          try {
            // Verifica se o dropdown já está inicializado
            if (!window.bootstrap.Dropdown.getInstance(element)) {
              // Inicializa o dropdown
              new window.bootstrap.Dropdown(element);
            }
          } catch (err) {
            console.warn('Erro ao inicializar dropdown:', err);
          }
        });
        
        console.log(`${dropdownElements.length} dropdowns inicializados com sucesso`);
      }
    }
  };
  
  useEffect(() => {
    // Verifica se o Bootstrap já está disponível
    if (typeof window !== 'undefined') {
      // Função para inicializar o Bootstrap quando disponível
      const initializeBootstrap = () => {
        if (window.bootstrap) {
          // Bootstrap já carregado, inicializa os dropdowns
          initializeDropdowns();
          return true;
        }
        
        // Verifica se o script do Bootstrap já foi carregado
        const existingScript = document.querySelector('script[src*="bootstrap.bundle.min.js"]');
        if (existingScript) {
          // Script já existe, aguarda um pouco mais
          setTimeout(initializeBootstrap, 200);
          return;
        }
        
        // Caso contrário, cria o script
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/bootstrap@5.2.3/dist/js/bootstrap.bundle.min.js';
        script.integrity = 'sha384-kenU1KFdBIe4zVF0s0G1M5b4hcpxyD9F7jL+jjXkk+Q2h455rYXK/7HAuoJl+0I4';
        script.crossOrigin = 'anonymous';
        script.async = true;
        
        // Adiciona ao head (mais seguro que body para evitar interferências)
        document.head.appendChild(script);
        
        script.onload = () => {
          console.log('Bootstrap carregado dinamicamente com sucesso');
          // Após carregar o Bootstrap, inicializa os dropdowns
          setTimeout(initializeDropdowns, 200);
        };
      };
      
      // Inicia o processo
      initializeBootstrap();
      
      // Adiciona um observer para detectar mudanças no DOM e reinicializar os dropdowns
      // quando novos elementos forem adicionados
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.addedNodes.length > 0) {
            // Verifica se algum dos nós adicionados contém elementos de dropdown
            for (let i = 0; i < mutation.addedNodes.length; i++) {
              const node = mutation.addedNodes[i];
              if (node.nodeType === 1 && // Elemento DOM
                  (node.hasAttribute('data-bs-toggle') || 
                   node.querySelector('[data-bs-toggle="dropdown"]'))) {
                // Inicializa os dropdowns novamente
                setTimeout(initializeDropdowns, 100);
                break;
              }
            }
          }
        });
      });
      
      // Observa mudanças no documento inteiro
      observer.observe(document.body, { 
        childList: true, 
        subtree: true 
      });
      
      // Cleanup function para evitar memory leaks
      return () => {
        observer.disconnect();
      };
    }
  }, []);

  // Este componente não renderiza nada visualmente
  return null;
};

export default BootstrapPreloader;
